import { useState } from 'react';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogScrollArea,
  DialogFooter,
} from '@/components/ui/dialog';
import { 
  CheckCircle, 
  Copy, 
  Download, 
  Globe, 
  Code,
  ExternalLink,
  FileText,
  Shield
} from 'lucide-react';
import { useTranslation } from 'react-i18next';

export interface BareAPISuccessIntegration {
  integration_name: string;
  binding_id: string;
  auth_config: {
    api_key: string;
    response_format: string;
    description?: string;
  };
  permissions: {
    can_read: boolean;
    can_write: boolean;
    can_search: boolean;
    can_delete: boolean;
  };
  rate_limits?: {
    requests_per_hour: number;
  };
}

export interface BareAPISuccessModalContentProps {
  onClose: () => void;
  integration: BareAPISuccessIntegration;
  spaceName: string;
}

export function BareAPISuccessModalContent({
  onClose,
  integration,
  spaceName,
}: BareAPISuccessModalContentProps) {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState('overview');
  const [copiedField, setCopiedField] = useState<string>('');

  const copyToClipboard = async (text: string, fieldName: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(fieldName);
      setTimeout(() => setCopiedField(''), 2000);
    } catch (err) {
      console.error('Failed to copy to clipboard:', err);
    }
  };

  // Socket-access URLs must hit the API host (same as VITE_API_BASE_URL), not the UI origin.
  const baseUrl = (import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000').replace(
    /\/$/,
    ''
  );
  const apiEndpoint = `${baseUrl}/api/v1/socket-access/${integration.binding_id}`;

  const getAPIUsageExample = () => {
    const examples = [];
    
    if (integration.permissions.can_write) {
      examples.push(`# Create a memory
curl -X POST "${apiEndpoint}/memories" \\
  -H "Content-Type: application/json" \\
  -H "Authorization: Bearer ${integration.auth_config.api_key}" \\
  -d '{
    "content": "Your memory content here",
    "tags": ["example", "api"],
    "category": "api-created",
    "importance": 5
  }'`);
      examples.push(`# Create audio memory (multipart — same as dashboard /memories/audio)
curl -X POST "${apiEndpoint}/memories/audio" \\
  -H "Authorization: Bearer ${integration.auth_config.api_key}" \\
  -F "audio_file=@recording.wav;type=audio/wav" \\
  -F "content=Voice note title" \\
  -F "tags=audio,bare-api" \\
  -F "source=bare_api_upload" \\
  -F "importance=5" \\
  -F "category=audio"`);
    }

    if (integration.permissions.can_search) {
      examples.push(`# Search memories (Simple - GET)
curl -X GET "${apiEndpoint}/memories/search?query=example&limit=10" \\
  -H "Authorization: Bearer ${integration.auth_config.api_key}"

# Search memories (Advanced - POST)
curl -X POST "${apiEndpoint}/memories/search" \\
  -H "Authorization: Bearer ${integration.auth_config.api_key}" \\
  -H "Content-Type: application/json" \\
  -d '{
    "query": "example",
    "limit": 10,
    "filter_tags": ["important"]
  }'`);
    }

    if (integration.permissions.can_read) {
      examples.push(`# List all memories
curl -X GET "${apiEndpoint}/memories" \\
  -H "Authorization: Bearer ${integration.auth_config.api_key}"`);
      
      examples.push(`# Get specific memory
curl -X GET "${apiEndpoint}/memories/MEMORY_ID" \\
  -H "Authorization: Bearer ${integration.auth_config.api_key}"`);
    }

    if (integration.permissions.can_delete) {
      examples.push(`# Delete a memory
curl -X DELETE "${apiEndpoint}/memories/MEMORY_ID" \\
  -H "Authorization: Bearer ${integration.auth_config.api_key}"`);
    }

    return examples.join('\n\n');
  };

  const downloadConfiguration = () => {
    const config = {
      integration_name: integration.integration_name,
      api_key: integration.auth_config.api_key,
      base_url: apiEndpoint,
      endpoints: {
        memories: `${apiEndpoint}/memories`,
        memories_audio: `${apiEndpoint}/memories/audio`,
        search: `${apiEndpoint}/memories/search`,
        memory_by_id: `${apiEndpoint}/memories/{memory_id}`,
        info: `${apiEndpoint}/info`
      },
      permissions: integration.permissions,
      rate_limits: integration.rate_limits,
      authentication: {
        type: 'Bearer',
        header: `Authorization: Bearer ${integration.auth_config.api_key}`
      }
    };

    const blob = new Blob([JSON.stringify(config, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `bare-api-${integration.integration_name.toLowerCase().replace(/\s+/g, '-')}-config.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const getPermissionsList = () => {
    const permissions = [];
    if (integration.permissions.can_read) permissions.push('Read');
    if (integration.permissions.can_write) permissions.push('Write');
    if (integration.permissions.can_search) permissions.push('Search');
    if (integration.permissions.can_delete) permissions.push('Delete');
    return permissions;
  };

  return (
    <div className="flex min-h-0 flex-1 flex-col">
        <DialogHeader className="flex-shrink-0 p-6 pb-4 pr-12">
          <DialogTitle className="text-xl font-semibold text-gray-900 flex items-center gap-3">
            <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
              <CheckCircle className="w-6 h-6 text-green-600" />
            </div>
            <div>
              <div className="text-xl text-gray-900">Bare API Integration Ready!</div>
              <div className="text-sm text-gray-600 font-normal">
                {integration.integration_name} • Connected to {spaceName}
              </div>
            </div>
          </DialogTitle>
        </DialogHeader>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="flex min-h-0 flex-1 flex-col w-full">
          <TabsList className="mx-6 grid w-full flex-shrink-0 grid-cols-4">
                          <TabsTrigger value="overview">{t('bareAPISuccess.tabs.overview', { ns: 'integrations' }) as string}</TabsTrigger>
            <TabsTrigger value="endpoints">API Endpoints</TabsTrigger>
            <TabsTrigger value="usage">Usage Examples</TabsTrigger>
            <TabsTrigger value="security">Security</TabsTrigger>
          </TabsList>

          <DialogScrollArea className="px-6">
          {/* Overview Tab */}
          <TabsContent value="overview" className="mt-6">
            <div className="space-y-6">
              {/* Integration Details */}
              <Card className="border-green-200 bg-green-50">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2 text-green-900">
                    <Globe className="w-5 h-5" />
                    API Integration Summary
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-green-700 font-medium">Integration Name:</span>
                      <div className="text-green-900">{integration.integration_name}</div>
                    </div>
                    <div>
                      <span className="text-green-700 font-medium">Connected Space:</span>
                      <div className="text-green-900">{spaceName}</div>
                    </div>
                    <div>
                      <span className="text-green-700 font-medium">Base URL:</span>
                      <div className="text-green-900 font-mono text-xs break-all overflow-x-auto">
                        {apiEndpoint}
                      </div>
                    </div>
                    <div>
                      <span className="text-green-700 font-medium">Permissions:</span>
                      <div className="text-green-900">
                        <div className="flex flex-wrap gap-1 mt-1">
                          {getPermissionsList().map(permission => (
                            <Badge key={permission} variant="outline" className="bg-green-100 text-green-800 border-green-300 text-xs">
                              {permission}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* What You Can Do */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">What You Can Do Now</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-green-600 mt-0.5" />
                      <div>
                        <h4 className="font-medium text-gray-900">Direct API Access</h4>
                        <p className="text-sm text-gray-600">
                          Make HTTP requests directly to your Memara memory system
                        </p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-green-600 mt-0.5" />
                      <div>
                        <h4 className="font-medium text-gray-900">Bearer Token Auth</h4>
                        <p className="text-sm text-gray-600">
                          Secure authentication with your generated API key
                        </p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-green-600 mt-0.5" />
                      <div>
                        <h4 className="font-medium text-gray-900">JSON Responses</h4>
                        <p className="text-sm text-gray-600">
                          Consistent JSON format for all API responses
                        </p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-green-600 mt-0.5" />
                      <div>
                        <h4 className="font-medium text-gray-900">Rate Limited</h4>
                        <p className="text-sm text-gray-600">
                          {integration.rate_limits?.requests_per_hour || 1000} requests per hour limit
                        </p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Quick Actions */}
              <div className="flex flex-wrap gap-3">
                <Button
                  onClick={() => copyToClipboard(integration.auth_config.api_key, 'api_key')}
                  variant="outline"
                  className="flex items-center gap-2"
                >
                  <Copy className="w-4 h-4" />
                  {copiedField === 'api_key' ? t('bareAPISuccess.buttons.copied', { ns: 'integrations' }) as string : t('bareAPISuccess.buttons.copyApiKey', { ns: 'integrations' }) as string}
                </Button>
                <Button
                  onClick={downloadConfiguration}
                  variant="outline"
                  className="flex items-center gap-2"
                >
                  <Download className="w-4 h-4" />
                  {t('bareAPISuccess.buttons.downloadConfig', { ns: 'integrations' }) as string}
                </Button>
                <Button
                  onClick={() => setActiveTab('usage')}
                  className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700"
                >
                  <Code className="w-4 h-4" />
                  {t('bareAPISuccess.buttons.viewExamples', { ns: 'integrations' }) as string}
                </Button>
              </div>
            </div>
          </TabsContent>

          {/* API Endpoints Tab */}
          <TabsContent value="endpoints" className="mt-6">
            <div className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Globe className="w-5 h-5" />
                    Available API Endpoints
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-3">
                    <div>
                      <label className="text-sm font-medium text-gray-700">Base URL</label>
                      <div className="mt-1 p-2 bg-gray-50 rounded border font-mono text-sm break-all">
                        {apiEndpoint}
                      </div>
                    </div>
                    
                    <div>
                      <label className="text-sm font-medium text-gray-700">Available Endpoints</label>
                      <div className="mt-1 space-y-2">
                        {integration.permissions.can_write && (
                          <div className="flex items-center gap-2 p-2 bg-gray-50 rounded border">
                            <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">POST</Badge>
                            <code className="font-mono text-sm text-gray-700">/memories</code>
                            <span className="text-sm text-gray-600">- Create new memories</span>
                          </div>
                        )}
                        {integration.permissions.can_read && (
                          <>
                            <div className="flex items-center gap-2 p-2 bg-gray-50 rounded border">
                              <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">GET</Badge>
                              <code className="font-mono text-sm text-gray-700">/memories</code>
                              <span className="text-sm text-gray-600">- List all memories</span>
                            </div>
                            <div className="flex items-center gap-2 p-2 bg-gray-50 rounded border">
                              <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">GET</Badge>
                              <code className="font-mono text-sm text-gray-700">/memories/:id</code>
                              <span className="text-sm text-gray-600">- Get specific memory</span>
                            </div>
                          </>
                        )}
                        {integration.permissions.can_search && (
                          <>
                            <div className="flex items-center gap-2 p-2 bg-gray-50 rounded border">
                              <Badge variant="outline" className="bg-purple-50 text-purple-700 border-purple-200">GET</Badge>
                              <code className="font-mono text-sm text-gray-700">/memories/search?query=example&limit=10</code>
                              <span className="text-sm text-gray-600">- Simple search</span>
                            </div>
                            <div className="flex items-center gap-2 p-2 bg-gray-50 rounded border">
                              <Badge variant="outline" className="bg-purple-50 text-purple-700 border-purple-200">POST</Badge>
                              <code className="font-mono text-sm text-gray-700">/memories/search</code>
                              <span className="text-sm text-gray-600">- Advanced search with filters</span>
                            </div>
                          </>
                        )}
                        {integration.permissions.can_delete && (
                          <div className="flex items-center gap-2 p-2 bg-gray-50 rounded border">
                            <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200">DELETE</Badge>
                            <code className="font-mono text-sm text-gray-700">/memories/:id</code>
                            <span className="text-sm text-gray-600">- Delete memory</span>
                          </div>
                        )}
                        <div className="flex items-center gap-2 p-2 bg-gray-50 rounded border">
                          <Badge variant="outline" className="bg-gray-50 text-gray-700 border-gray-200">GET</Badge>
                          <code className="font-mono text-sm text-gray-700">/info</code>
                          <span className="text-sm text-gray-600">- Integration info</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Usage Examples Tab */}
          <TabsContent value="usage" className="mt-6">
            <div className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Code className="w-5 h-5" />
                    API Usage Examples
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <label className="text-sm font-medium text-gray-700">cURL Examples</label>
                      <Button
                        onClick={() => copyToClipboard(getAPIUsageExample(), 'curl_examples')}
                        variant="outline"
                        size="sm"
                      >
                        <Copy className="w-4 h-4 mr-1" />
                        {copiedField === 'curl_examples' ? t('bareAPISuccess.buttons.copied', { ns: 'integrations' }) as string : t('bareAPISuccess.buttons.copy', { ns: 'integrations' }) as string}
                      </Button>
                    </div>
                    <pre className="p-4 bg-gray-900 text-gray-100 rounded-lg text-xs overflow-x-auto max-w-full whitespace-pre-wrap break-words">
                      {getAPIUsageExample()}
                    </pre>
                  </div>

                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <h4 className="font-medium text-blue-900 mb-2">Response Format</h4>
                    <div className="text-sm text-blue-800 space-y-1">
                      <p>All responses are in JSON format with consistent structure:</p>
                      <pre className="text-xs bg-blue-100 p-2 rounded mt-2">
{`{
  "status": "success",
  "data": { ... },
  "message": "Optional message"
}`}
                      </pre>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Security Tab */}
          <TabsContent value="security" className="mt-6">
            <div className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Shield className="w-5 h-5" />
                    Security Information
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <label className="text-sm font-medium text-gray-700">API Key</label>
                    <div className="mt-1 flex items-center gap-2">
                      <code className="flex-1 p-2 bg-gray-50 rounded border font-mono text-sm break-all">
                        {integration.auth_config.api_key}
                      </code>
                      <Button
                        onClick={() => copyToClipboard(integration.auth_config.api_key, 'api_key')}
                        variant="outline"
                        size="sm"
                      >
                        <Copy className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>

                  <div>
                    <label className="text-sm font-medium text-gray-700">Authentication Header</label>
                    <div className="mt-1 p-2 bg-gray-50 rounded border font-mono text-sm">
                      Authorization: Bearer {integration.auth_config.api_key}
                    </div>
                  </div>

                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                    <h4 className="font-medium text-amber-900 mb-2">Security Best Practices</h4>
                    <div className="text-sm text-amber-800 space-y-1">
                      <p>🔒 Store your API key securely and never commit it to version control</p>
                      <p>🔒 Use environment variables to manage API keys in production</p>
                      <p>🔒 Regularly rotate your API keys for enhanced security</p>
                      <p>🔒 Monitor API usage and set up alerts for unusual activity</p>
                      <p>🔒 Use HTTPS for all API requests to encrypt data in transit</p>
                    </div>
                  </div>

                  <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                    <h4 className="font-medium text-red-900 mb-2">Important Security Notes</h4>
                    <div className="text-sm text-red-800 space-y-1">
                      <p>⚠️ This API key provides access to all memories in this socket</p>
                      <p>⚠️ Anyone with this key can perform allowed operations on your memories</p>
                      <p>⚠️ Rate limits: {integration.rate_limits?.requests_per_hour || 1000} requests per hour</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
          </DialogScrollArea>
        </Tabs>

        <DialogFooter variant="scrollable" className="justify-between px-6 pb-6">
          <div className="flex gap-2">
            <Button
              onClick={() => window.open('/integrations/bare-api', '_blank')}
              variant="outline"
              size="sm"
            >
              <FileText className="w-4 h-4 mr-1" />
              Documentation
            </Button>
            <Button
              onClick={() => window.open(`${apiEndpoint}/info`, '_blank')}
              variant="outline"
              size="sm"
            >
              <ExternalLink className="w-4 h-4 mr-1" />
              {t('bareAPISuccess.buttons.testApi', { ns: 'integrations' }) as string}
            </Button>
          </div>
          <Button onClick={onClose} className="bg-green-600 hover:bg-green-700">
            Done
          </Button>
        </DialogFooter>
    </div>
  );
}

interface BareAPISuccessModalProps {
  isOpen: boolean;
  onClose: () => void;
  integration: BareAPISuccessIntegration;
  spaceName: string;
}

export function BareAPISuccessModal({
  isOpen,
  onClose,
  integration,
  spaceName,
}: BareAPISuccessModalProps) {
  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <DialogContent variant="scrollable" className="max-w-5xl bg-white">
        <BareAPISuccessModalContent
          integration={integration}
          onClose={onClose}
          spaceName={spaceName}
        />
      </DialogContent>
    </Dialog>
  );
}
